import type { AuditEntry } from '@pedigreesmart/api-client'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './settings.css'

/**
 * Il registro degli accessi.
 *
 * Le voci si scrivevano dal primo giorno e non c'era modo di leggerle: la
 * domanda a cui questo elenco risponde — «chi ha cancellato i contatti di
 * quella persona?», «chi è entrato nel conto di quel dipendente sabato
 * notte?» — arriva sempre dopo il fatto, e finora l'unica risposta possibile
 * era chiedere a noi di guardare nel database.
 *
 * **Non si filtra per testo libero.** Le azioni sono un elenco chiuso e
 * l'endpoint dice quali esistono davvero: un campo di ricerca inviterebbe a
 * cercare `cancellazione` in un registro che scrive `person.anonymized`, e
 * un risultato vuoto verrebbe letto come «non è successo niente».
 *
 * Non c'è nessun pulsante per cancellare o correggere una voce, e non è una
 * mancanza: un registro che si può ripulire dimostra soltanto che nessuno
 * l'ha ripulito.
 */
export function AuditLog() {
  const { resources } = useSession()
  const [azione, setAzione] = useState('')
  const [da, setDa] = useState('')
  const [a, setA] = useState('')
  const [pagina, setPagina] = useState(1)

  const registro = useQuery({
    queryKey: ['audit', azione, da, a, pagina],
    queryFn: ({ signal }) =>
      resources.audit.list(
        {
          ...(azione ? { action: azione } : {}),
          ...(da ? { from: da } : {}),
          ...(a ? { to: a } : {}),
          page: pagina,
        },
        signal,
      ),
    // L'elenco cambia sotto le mani mentre lo si guarda: tenere i dati
    // precedenti evita che la tabella sparisca a ogni cambio di filtro.
    placeholderData: (precedente) => precedente,
  })

  const cambia = (imposta: (valore: string) => void) => (valore: string) => {
    imposta(valore)
    setPagina(1)
  }

  const pagine = registro.data ? Math.ceil(registro.data.total / registro.data.per_page) : 1

  return (
    <section aria-labelledby="titolo-registro">
      <div className="elenco__testa">
        <h1 id="titolo-registro">Registro degli accessi</h1>
      </div>

      <div className="registro__filtri">
        <label>
          <span>Azione</span>
          <select
            value={azione}
            onChange={(evento) => {
              cambia(setAzione)(evento.target.value)
            }}
          >
            <option value="">Tutte</option>
            {(registro.data?.actions ?? []).map((valore) => (
              <option key={valore} value={valore}>
                {etichetta(valore)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Dal</span>
          <input
            type="date"
            value={da}
            onChange={(evento) => {
              cambia(setDa)(evento.target.value)
            }}
          />
        </label>

        <label>
          <span>Al</span>
          <input
            type="date"
            value={a}
            onChange={(evento) => {
              cambia(setA)(evento.target.value)
            }}
          />
        </label>
      </div>

      {registro.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere il registro.
        </p>
      )}

      {registro.isPending && <p className="messaggio">Caricamento…</p>}

      {registro.data && registro.data.items.length === 0 && (
        <p className="messaggio">Nessuna voce con questi filtri.</p>
      )}

      {registro.data && registro.data.items.length > 0 && (
        <>
          <div className="tabella-scorrevole">
            <table>
              <thead>
                <tr>
                  <th scope="col">Quando</th>
                  <th scope="col">Chi</th>
                  <th scope="col">Cosa</th>
                  <th scope="col">Su</th>
                  <th scope="col">Da dove</th>
                </tr>
              </thead>
              <tbody>
                {registro.data.items.map((voce) => (
                  <tr key={voce.id}>
                    <td>{quando(voce.created_at)}</td>
                    <td>{chi(voce)}</td>
                    <td>
                      {etichetta(voce.action)}
                      {dettaglio(voce) && <div className="registro__dettaglio">{dettaglio(voce)}</div>}
                    </td>
                    <td>{voce.entity_type ? `${voce.entity_type} ${voce.entity_id ?? ''}`.trim() : '—'}</td>
                    {/* L'indirizzo distingue «il collega dalla scrivania
                        accanto» da «qualcuno da fuori con la sua password»,
                        che è la differenza per cui si apre questa pagina. */}
                    <td className="registro__ip">{voce.ip_address ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav className="paginazione" aria-label="Pagine">
            <button
              type="button"
              data-variante="secondario"
              disabled={pagina <= 1}
              onClick={() => {
                setPagina((corrente) => corrente - 1)
              }}
            >
              Precedente
            </button>
            <span aria-live="polite">
              {registro.data.total.toLocaleString('it-IT')} voci — pagina {pagina} di {pagine}
            </span>
            <button
              type="button"
              data-variante="secondario"
              disabled={pagina >= pagine}
              onClick={() => {
                setPagina((corrente) => corrente + 1)
              }}
            >
              Successiva
            </button>
          </nav>
        </>
      )}
    </section>
  )
}

/**
 * Chi ha agito.
 *
 * Un tentativo di accesso fallito non ha un utente quando l'indirizzo non
 * esiste: quelli restano fuori dal registro dell'associazione, ma la colonna
 * deve reggere comunque il caso senza mostrare «undefined».
 */
function chi(voce: AuditEntry): string {
  return voce.user ? voce.user.name : '—'
}

/**
 * Le azioni in italiano.
 *
 * Quelle che non conosciamo restano come sono, con il punto al posto di uno
 * spazio: meglio `declaration.qualcosa` che una traduzione inventata che
 * nasconde di non sapere cosa sia successo.
 */
function etichetta(azione: string): string {
  const noti: Record<string, string> = {
    login: 'Accesso',
    login_failed: 'Accesso fallito',
    impersonate: 'Accesso per conto di',
    password_reset: 'Password reimpostata',
    totp_enabled: 'Secondo fattore attivato',
    'person.exported': 'Anagrafica estratta',
    'person.anonymized': 'Contatti cancellati',
    'declaration.submitted': 'Denuncia presentata',
    'declaration.approved': 'Denuncia approvata',
    'declaration.rejected': 'Denuncia respinta',
    'declaration.registered': 'Denuncia registrata',
    'certificate.issued': 'Certificato emesso',
    'parentage.updated': 'Genealogia modificata',
    'animal.deregistered': 'Soggetto radiato',
    unscoped_access: 'Accesso senza associazione',
  }

  return noti[azione] ?? azione
}

/** Il motivo, quando c'è: è la parte che spiega la riga. */
function dettaglio(voce: AuditEntry): string | null {
  const motivo = voce.changes['reason']

  return typeof motivo === 'string' && motivo !== '' ? motivo : null
}

function quando(iso: string): string {
  const momento = new Date(iso)

  return Number.isNaN(momento.getTime()) ? iso : momento.toLocaleString('it-IT')
}
